import { firstValueFrom } from "rxjs";
import { FormField } from "src/app/components/default/form/form-field";
import {
  FieldLink,
  FieldSSLink,
  FormFieldResponse,
  FormListValue,
  FormListValueGroup,
  GridLayout,
  GridValue,
  ProgressResponse,
} from "src/app/components/default/form/form.interface";
import { DialogService } from "src/app/components/global/dialog/dialog.service";
import { LinkData } from "src/app/components/global/prefixes/templates/template-link/template-link.component";
import { MarkerData } from "src/app/components/global/prefixes/templates/template-map/template-map.component";
import { ErrorMessageComponent } from "src/app/components/global/snackbar/impl/error-message/error-message.component";
import { FileManager } from "src/classes/FilesManager";
import { PostResponse, PostResult } from "src/interfaces/post-request/post-request";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { SessionService } from "src/services/session.service";
import { ComponentSize, ContentPartType } from "./content.component";
import { ContentForm } from "./impl/content-form";

export class ContentPart {
  public id: string;
  public type: ContentPartType;
  public title: string;
  public size: ComponentSize;
  public gridStyle: string | null;

  protected dialog: DialogService;
  protected application: ApplicationService;
  protected session: SessionService;
  protected http: HttpService;

  public contentid: string;
  public contentpartid: string;

  public tabindex: string | null;
  public contentPostUrl: string;

  public constructor(
    id: string,
    type: ContentPartType,
    dialog: DialogService,
    application: ApplicationService,
    session: SessionService,
    http: HttpService,
    contentPostUrl: string,
  ) {
    this.id = id;
    this.type = type;
    this.title = "";
    this.size = "auto";
    this.gridStyle = null;
    this.dialog = dialog;
    this.application = application;
    this.session = session;
    this.http = http;
    this.contentid = "";
    this.contentpartid = "";
    this.contentPostUrl = contentPostUrl;
    this.tabindex = null;
  }

  /**
   * Set correct data of image prefixes
   * @param value
   * @returns
   */
  public parseImage(value: string): { height: number; width: number } {
    return {
      height: parseInt(value.split("|")[1] || "0"),
      width: parseInt(value.split("|")[2] || "0"),
    };
  }

  /**
   * Set correct data of select prefixes
   * @param values
   * @returns
   */
  public parseSelect(values: FormListValue[]): { listvalues: { label: string; value: string }[] } {
    return {
      listvalues: values
        ? values.map((option) => ({
            label: option.d,
            disabled: option.o == "true",
            value: option.v,
          }))
        : [],
    };
  }

  /**
   * Set correct data for labels
   * @param label
   * @returns
   */
  public parseLabel(label: string): { label: string } {
    return {
      label,
    };
  }

  public parseGridStyle(grid: GridLayout): string {
    return `grid-area: ${grid.row} / ${grid.col} / ${grid.row + 1} / ${grid.col + grid.size};`;
  }

  /**
   * Set correct data of select groups
   * @param values
   * @returns
   */
  public parseSelectGroups(values: FormListValueGroup[]): {
    listvaluesgroups: {
      group: string;
      options: {
        label: string;
        value: string;
      }[];
    }[];
  } {
    return {
      listvaluesgroups: values
        ? values.map((group) => ({
            group: group.group,
            options: this.parseSelect(group.listvalues).listvalues,
          }))
        : [],
    };
  }

  /**
   * Set correct data of checkbox prefixes
   * @param value
   * @returns
   */
  public parseCheckbox(value: { value: string; description: string }[]): {
    checkbox: {
      label: string;
    }[];
  } {
    return {
      checkbox: value.map((val) => ({
        label: val.description,
      })),
    };
  }

  /**
   * Set correct data of table prefixes
   * @param data
   * @returns
   */
  public parseTable(data: FormFieldResponse["tabledata"]): { tabledata: FormFieldResponse["tabledata"] } {
    return {
      tabledata: data,
    };
  }

  /**
   * Set correct data of image prefixes
   * @param value
   * @returns
   */
  public parseLink(links: FieldLink[] | FieldSSLink[]): { links: LinkData[] } {
    return {
      links: links.map((v: FieldLink | FieldSSLink) => {
        if (this.isSSLink(v)) {
          return { value: v.display, link: v.link, type: v.type };
        } else {
          return v as LinkData;
        }
      }),
    };
  }

  /**
   * Sanitize strings/numbers to correct booleans
   * @param value
   * @returns
   */
  public sanitizeBoolean(value: unknown): boolean {
    if (typeof value == "string") {
      return value.toLowerCase() == "true";
    } else {
      return !!value;
    }
  }

  /**
   * Sanitize strings/numbers to correct booleans
   * @param value
   * @returns
   */
  public sanitizeFloat(value: unknown): number {
    if (typeof value == "string") {
      return parseFloat(value.replace(",", ""));
    } else {
      return parseFloat(`${value}`);
    }
  }

  /**
   * Deconstruct content part
   */
  public deconstruct(): void {
    console.log("Deconstructing content part =>", {
      class: this,
    });
  }

  public parseProgress(value: ProgressResponse): Record<string, unknown> {
    console.warn("parseing progress => ", { value });
    return {
      current: this.sanitizeFloat(value.decCurrent),
      end: this.sanitizeFloat(value.decEnd),
      start: this.sanitizeFloat(value.decStart),
      type: value.gtpType,
      unit: value.omsUnit,
    };
  }

  /**
   * Get values as values that api expects
   * @param field
   */
  public async getFieldValue(field: FormField): Promise<string | Blob | string[] | Blob[]> {
    const value = await firstValueFrom(field.value);

    switch (field.template) {
      case "jan":
      case "jn3":
        return (<boolean>value).toString();

      case "dis":
      case "di3":
        return value != null ? (<Date>value).toISOString() : "";

      case "gri":
      case "gr2":
      case "gr3":
        return JSON.stringify(
          (<GridValue[]>value).map((value) => ({
            id: value.id,
            ck: value.checked,
            ot: value.text || undefined,
            df: value.dateFrom || "",
            dt: value.dateTo || "",
          })),
        );

      case "gep":
        console.log(value);
        return JSON.stringify(
          (<MarkerData[]>value).map(({ location: { lat, lng } }) => ({
            Latitude: typeof lat === "function" ? lat() : lat,
            Longitude: typeof lng === "function" ? lng() : lng,
          })),
        );

      case "geo": {
        console.log(value);
        const v = <MarkerData[] | null>value;
        if (!v?.length) return "{}";
        return JSON.stringify(
          v
            .map(({ location: { lat, lng } }) => ({
              lat: typeof lat === "function" ? lat() : lat,
              lon: typeof lng === "function" ? lng() : lng,
            }))
            .at(0),
        );
      }

      case "ti3":
      case "ti4":
        return value ? `${value}:00.000` : "";

      case "da2":
      case "dat":
        return `${value ? this.formatDate(<string>value) : value}`;

      case "sao": {
        return this.getSAOStringValue(<{ labelSuffix: string; value: { label: string; value: string }[] }[]>value);
      }

      case "fil": {
        if (value instanceof FileList) {
          console.log("FileList", value);
          return Array.from(value);
          // const files: string[] = [];
          // for (const file of Array.from(value)) {
          //   const res = await this.toBase64(file);
          //   files.push(res);
          // }
          // return files;
        } else if (value instanceof File) {
          console.log("File", value);
          return value;
          // return this.toBase64(value);
        } else {
          console.log("Unknown", value);
          return "";
        }
      }

      default:
        return <string>value || "";
    }
  }

  /**
   * Parse Content-Disposition in to a filename
   * @param headers Response headers
   * @returns Parsed Filename
   */
  protected getFilenameFromHeader(headers: Headers): string | null {
    const header = headers.get("Content-Disposition");
    const start = /filename=(")?/;
    const end = /(")?;/;
    if (header) {
      return header.split(start).pop()?.split(end).shift() || null;
    }
    return null;
  }

  protected isSSLink(link: FieldLink | FieldSSLink): link is FieldSSLink {
    return "display" in link;
  }

  /**
   * Event listener on form action save
   */
  protected async onSave(
    fields: FormField[],
    filename: string | null,
    dialogRef?: string,
    download = false,
    form?: ContentForm,
    ignoreMessages = false,
  ): Promise<void> {
    try {
      if (filename || download) {
        const headers = new Headers();
        const response = await this.save<Blob>(fields, form, "blob", headers);

        if (response) {
          FileManager.download({
            blob: response,
            label: filename || this.getFilenameFromHeader(headers) || "null",
          });
        } else {
          throw new Error("Received an empty response");
        }
      } else {
        const response = await this.save<PostResponse>(fields, form);
        const parsed = <PostResult>JSON.parse(response.postDataAsJSON);

        if (this.session.isInvalid(parsed)) {
          const result = await this.session.handleSessionEvent(<Record<string, string>>(<unknown>parsed));
          if (result) {
            return this.onSave(fields, filename, dialogRef, download);
          }
        }

        if (parsed.result === "fail" && !parsed.messages?.length) {
          this.application.snackbar.open<string>(ErrorMessageComponent, "ERRORS.RESPONSE");
          this.application.router.navigate(["/app/home/"]);
        } else if (!ignoreMessages) {
          this.application.onMessage(parsed.messages ?? []);
        }

        if (dialogRef) {
          this.dialog.close(dialogRef);
        }
      }
    } catch (e) {
      console.error("Unable to save", e);
    }
  }

  /**
   * Save and send the form
   */
  protected async save<T>(fields: FormField[], form?: ContentForm, responseType = "json", headersOut?: Headers): Promise<T> {
    console.log("Posting to", this.contentPostUrl);
    const formdata = await this.getFormData(fields, form);
    return this.http.send<T>(this.contentPostUrl, formdata, {
      observe: responseType == "blob" ? <"body">"response" : "body",
      responseType: <"json">responseType,
      responseHeadersOut: headersOut,
    });
  }

  /**
   * Get formdata of fields
   * @param fields
   * @returns
   */
  protected async getFormData(fields: FormField[], form?: ContentForm): Promise<FormData> {
    const formdata = new FormData();
    const _contentid: string | undefined = form?.contentid ?? this.contentid;
    const _contentpartid: string | undefined = form?.contentpartid ?? this.contentpartid;

    if (!_contentid) throw new Error("Missing contentid in content-form");
    if (!_contentpartid) {
      throw new Error("Missing contentpartid in content-form");
    }

    formdata.append("tabindex", this.tabindex || "");
    formdata.append("contentid", _contentid);
    formdata.append("contentpartid", _contentpartid);

    for (const field of fields) {
      const value = await this.getFieldValue(field);
      if (Array.isArray(value)) {
        for (const _value of value) {
          formdata.append(`${field.name}[]`, _value);
        }
      } else {
        formdata.append(field.name, value);
      }
    }

    return formdata;
  }

  private formatDate(value: string): string {
    const split = value.split("-");
    if (split[0] && split[0].length === 4) {
      split.reverse();
    }
    return split
      .map((value, index) => {
        if (index === split.length - 1) return value;
        return value.padStart(2, "0");
      })
      .join("-");
  }

  private toBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (): void => resolve(reader.result as string);
      reader.onerror = (error): void => reject(error);
    });
  }

  private getSAOStringValue(value: { labelSuffix: string; value: { label: string; value: string }[] }[]): string {
    let lwValues: Record<string, string> = {};
    for (const group of value) {
      lwValues = {
        ...lwValues,
        [group.labelSuffix]: group.value.filter((groupValue) => groupValue.label === "LW")[0].value,
      };
    }

    const mappedValue = value.map((group) => {
      return {
        n: group.labelSuffix,
        v: group.value
          .filter((groupValue) => groupValue.label !== "LW")
          .map((groupValue) => {
            return {
              n: groupValue.label,
              uu4: groupValue.value,
              pr2: lwValues[group.labelSuffix],
            };
          }),
      };
    });
    return JSON.stringify(mappedValue);
  }
}
